import React, { useEffect, useId, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useTerminology } from '../../hooks/useTerminology';
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
  width?: 'sm' | 'md' | 'lg';
}

/* Roomier than before: a cramped dialog is unreadable at 200% zoom. */
const WIDTHS = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl'
};
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  width = 'md'
}: ModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  /* A dialog title is the most consequential place a concept is named — it is
     what someone reads before confirming — so it resolves terminology too. */
  const {
    phrase
  } = useTerminology();

  /**
   * `onClose` is almost always an inline arrow, so it is a new function on
   * every render of the calling page. Depending on it directly would re-run
   * the effect below on every one of those renders — and each re-run pulls
   * focus back to the close button, throwing someone out of the field they
   * are typing in (and closing the keyboard on a phone). The handler is held
   * in a ref so the effect depends only on the dialog opening.
   */
  const closeHandler = useRef(onClose);
  closeHandler.current = onClose;
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') closeHandler.current();
    }
    window.addEventListener('keydown', onKey);
    /* Focus moves into the dialog ONCE, when it opens, so keyboard users are
       not left behind it — and never again while it is open. */
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      window.removeEventListener('keydown', onKey);
      cancelAnimationFrame(frame);
    };
  }, [open]);
  return <AnimatePresence data-id="element-5895" data-mp="3n34afh66gc3i">
      {open && <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center" data-id="element-5896" data-mp="3n46yzig122g9">
          <motion.div className="fixed inset-0 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} onClick={onClose} data-id="element-5897" data-mp="3i9t0h762jf7d" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby={headingId}
      /* Clamped to the visible viewport and scrolled internally, so a long
         form still shows its header and actions once the keyboard opens. */ className={`relative z-10 flex max-h-[calc(100dvh-2rem)] w-full flex-col ${WIDTHS[width]} rounded-card border border-line bg-surface shadow-pop`} initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 4
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-5898" data-mp="9vkeihy1xmo6">
            <header className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4 sm:px-6" data-id="element-5899" data-mp="1074iziuz0f0s">
              <div className="min-w-0" data-id="element-5900" data-mp="153v10bqc5kou">
                <h2 id={headingId} className="text-h3 font-semibold text-ink" data-id="element-5901" data-mp="19vmizxvfei3d">
                  {phrase(title)}
                </h2>
                {description && <p className="mt-1.5 text-body text-ink-muted" data-id="element-5902" data-mp="1ex3fziashkeo">{phrase(description)}</p>}
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close dialog" className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas hover:text-ink" data-id="element-5903" data-mp="1nvx9gzstpuo0">
                <XIcon aria-hidden className="h-5 w-5" data-id="element-5904" data-mp="3umdjf9aru8s1" />
              </button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-body sm:px-6 sm:py-5" data-id="element-5905" data-mp="2za41jsi3l57p">
              {children}
            </div>
            {footer && <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-4 sm:px-6" data-id="element-5906" data-mp="3993bdeeln6wa">
                {footer}
              </footer>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}